import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FiUser, FiCalendar, FiTruck } from 'react-icons/fi'

import api from '../../../utils/api'
import Button from '../../../shared/components/Button/Button'
import AuthImage from '../../../shared/components/AuthImage/AuthImage'

/* Hooks */
import TimestampToString from '../../../shared/functions/TimestampToString'

import './OrderReport.css'

// Texto de identificação do item (categoria + tecido + sexo), sem preço — mesma ideia
// de descriptionText() em ProposalPDFContent.js, mas sem valores, pra uso de produção.
function descriptionText(item) {
    const parts = []
    if (item.category) parts.push(item.category.label || item.category.name)
    if (item.cloth) parts.push(item.cloth.label || item.cloth.name)
    if (item.sex) parts.push(item.sex.label)
    return parts.filter(Boolean).join(' · ')
}

function OrderReport() {
    const { id } = useParams()
    const [order, setOrder] = useState(null)
    const [client, setClient] = useState({})
    const [load, setLoad] = useState(false)
    const { timeStampToDDMMAAAA } = TimestampToString()

    useEffect(() => {
        const token = localStorage.getItem('token') || ''
        api.get(`order/budget/get/${id}`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setOrder(response.data.budget)
            setClient(response.data.budget.client || {})
            setLoad(true)
            // Também vira o nome sugerido do arquivo ao salvar como PDF pelo diálogo de impressão.
            document.title = `Pedido #${response.data.budget.code} - ${response.data.budget.client?.name || ''}`
        })
    }, [id])

    if (!load) {
        return <div className="pg-loading">Carregando relatório do pedido...</div>
    }

    const items = order.orderItemArray || []

    return (
        <div className="order-report-page">
            <div className="order-report-toolbar order-report-no-print pg-sticky-top">
                <Button variant="print" onClick={() => window.print()}>Imprimir / Exportar PDF</Button>
            </div>

            <div className="order-report-print-area">
                <div className="order-report-header">
                    <div className="order-report-header-title">
                        <p className="order-report-title">{client.name} — Pedido #{order.code}</p>
                        <span className="order-report-title-accent" />
                    </div>

                    <div className="order-report-header-grid">
                        <div className="order-report-header-block">
                            <p className="order-report-block-title">Dados do Pedido</p>
                            <div className="order-report-field-list">
                                {order.seller?.name && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label"><FiUser size={11} /> Vendedor</span>
                                        <span className="order-report-field-value">{order.seller.name}</span>
                                    </div>
                                )}
                                {order.orderEntryDate && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label"><FiCalendar size={11} /> Entrada</span>
                                        <span className="order-report-field-value">{timeStampToDDMMAAAA(order.orderEntryDate)}</span>
                                    </div>
                                )}
                                {order.deliveryForecast && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label"><FiTruck size={11} /> Previsão de Entrega</span>
                                        <span className="order-report-field-value">{order.deliveryForecast}</span>
                                    </div>
                                )}
                                {order.nature && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">Natureza</span>
                                        <span className="order-report-field-value">{order.nature === 'order' ? 'Pedido' : order.nature}</span>
                                    </div>
                                )}
                                {order.step && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">Etapa</span>
                                        <span className="order-report-step-badge">{order.step}</span>
                                    </div>
                                )}
                                {order.status && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">Status</span>
                                        <span className="order-report-status-badge">{order.status}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="order-report-header-block">
                            <p className="order-report-block-title">Dados do Cliente</p>
                            <div className="order-report-field-list">
                                {client.name && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">Nome</span>
                                        <span className="order-report-field-value">{client.name}</span>
                                    </div>
                                )}
                                {client.personType !== 'fisica' && (client.corporateName || client.fantasyName) && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">Razão Social</span>
                                        <span className="order-report-field-value">{client.corporateName}{client.fantasyName && ` (${client.fantasyName})`}</span>
                                    </div>
                                )}
                                {(client.CPF || client.CNPJ) && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">{client.personType === 'fisica' ? 'CPF' : 'CNPJ'}</span>
                                        <span className="order-report-field-value">{client.personType === 'fisica' ? client.CPF : client.CNPJ}</span>
                                    </div>
                                )}
                                {(client.contacts || []).length > 0 && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">Contatos</span>
                                        <span className="order-report-field-value">
                                            {client.contacts.map((c) => `${c.name} — ${c.phone}`).join(' | ')}
                                        </span>
                                    </div>
                                )}
                                {client.email && (
                                    <div className="order-report-field">
                                        <span className="order-report-field-label">E-mail</span>
                                        <span className="order-report-field-value">{client.email}</span>
                                    </div>
                                )}
                                {client.address && (
                                    <div className="order-report-field order-report-field-wide">
                                        <span className="order-report-field-label">Endereço</span>
                                        <span className="order-report-field-value">
                                            {client.address}{client.number && `, ${client.number}`}{client.complement && ` - ${client.complement}`}{client.district && ` - ${client.district}`}
                                            {(client.city || client.state) && ` — ${[client.city, client.state].filter(Boolean).join('/')}`}
                                            {client.cep && ` · CEP ${client.cep}`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="order-report-items">
                    {items.map((item, index) => (
                        <div key={item._id || index} className="order-report-item">
                            <div className="order-report-item-header">
                                <span className="order-report-item-number">{index + 1}</span>
                                <div className="order-report-item-header-text">
                                    <span className="order-report-item-code">#{item.code}</span>
                                    <span className="order-report-item-desc">{item.simpleProduct ? item.description : descriptionText(item)}</span>
                                </div>
                                <span className="order-report-item-total">{item.totalQuantity} un.</span>
                            </div>

                            {item.simpleProduct ? (
                                item.obs && (
                                    <div className="order-report-section">
                                        <p className="order-report-section-title">Observação</p>
                                        <p className="order-report-obs">{item.obs}</p>
                                    </div>
                                )
                            ) : (
                                <>
                                    {(item.cloth || item.provider) && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Tecido / Consumo</p>
                                            <div className="order-report-cloth-info">
                                                {item.cloth?.name && <span>{item.cloth.name}</span>}
                                                {item.provider?.name && <span>Fornecedor: {item.provider.name}</span>}
                                                {item.measure && <span>{item.measure} {item.unit?.label || item.unit?.value} / peça</span>}
                                            </div>
                                        </div>
                                    )}

                                    {item.colors?.length > 0 && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Cores</p>
                                            <div className="order-report-swatch-grid">
                                                {item.colors.map((c) => (
                                                    <div key={c._id} className="order-report-swatch">
                                                        {c.image && <AuthImage className="order-report-swatch-img" src={`/images/color/${c.image}`} alt={c.name} />}
                                                        <span className="order-report-swatch-name">{c.name}{c.ref && ` (${c.ref})`}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.sizes?.some((s) => parseInt(s.quantity) > 0) && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Grade / Tamanhos</p>
                                            <table className="order-report-size-table">
                                                <tbody>
                                                    <tr>
                                                        {item.sizes.filter((s) => parseInt(s.quantity) > 0).map((s) => (
                                                            <td key={s._id} className="order-report-size-name">{s.name}</td>
                                                        ))}
                                                        <td className="order-report-size-name order-report-size-total-label">Total</td>
                                                    </tr>
                                                    <tr>
                                                        {item.sizes.filter((s) => parseInt(s.quantity) > 0).map((s) => (
                                                            <td key={s._id} className="order-report-size-qty">{s.quantity}</td>
                                                        ))}
                                                        <td className="order-report-size-qty order-report-size-total-value">{item.totalQuantity}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {item.additive?.some((a) => a.checked) && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Adicionais</p>
                                            <div className="order-report-chip-list">
                                                {item.additive.filter((a) => a.checked).map((a) => (
                                                    <span key={a._id} className="order-report-chip">{a.name}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.accessoriesSelected?.length > 0 && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Acessórios</p>
                                            <div className="order-report-visual-grid">
                                                {item.accessoriesSelected.map((a, i) => (
                                                    <div key={a.id || i} className="order-report-visual-card">
                                                        {a.image && <AuthImage className="order-report-visual-img" src={`/images/accessory/${a.image}`} alt={a.name} />}
                                                        <span className="order-report-visual-name">{a.name}</span>
                                                        {a.description && <span className="order-report-visual-desc">{a.description}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.prints?.length > 0 && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Estampas</p>
                                            <div className="order-report-visual-grid">
                                                {item.prints.map((p, i) => (
                                                    <div key={p._id || i} className="order-report-visual-card">
                                                        {p.image && <AuthImage className="order-report-visual-img" src={`/images/clientImg/${p.image}`} alt="Estampa" />}
                                                        {p.position?.label && <span className="order-report-visual-name">{p.position.label}</span>}
                                                        {p.description && <span className="order-report-visual-desc">{p.description}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.embroidery?.length > 0 && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Bordados</p>
                                            <div className="order-report-visual-grid">
                                                {item.embroidery.map((e, i) => (
                                                    <div key={e._id || i} className="order-report-visual-card">
                                                        {e.image && <AuthImage className="order-report-visual-img" src={`/images/clientImg/${e.image}`} alt="Bordado" />}
                                                        {e.position?.label && <span className="order-report-visual-name">{e.position.label}</span>}
                                                        {e.description && <span className="order-report-visual-desc">{e.description}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.clientImgsSelected?.length > 0 && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Imagens de Referência do Cliente</p>
                                            <div className="order-report-visual-grid">
                                                {item.clientImgsSelected.map((img, i) => (
                                                    <div key={img._id || i} className="order-report-visual-card">
                                                        <AuthImage className="order-report-visual-img" src={`/images/clientImg/${img.image}`} alt="Referência do cliente" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.obs && (
                                        <div className="order-report-section">
                                            <p className="order-report-section-title">Observação</p>
                                            <p className="order-report-obs">{item.obs}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {order.proposalObs && (
                    <div className="order-report-footer">
                        <p className="order-report-section-title">Observações Gerais do Pedido</p>
                        <p className="order-report-obs">{order.proposalObs}</p>
                    </div>
                )}
            </div>
        </div>
    )
}

export default OrderReport
